import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Cookie, Shield, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    CONSENT_CHANGE_EVENT,
    OPEN_COOKIE_PREFS_EVENT,
    getConsentState,
    grantAnalyticsConsent,
    isOptedOut,
    isTrackablePath,
    setAnalyticsConsent,
    withdrawAnalyticsConsent,
} from "@/lib/analytics";

/**
 * CookieConsent — bottom-anchored dark editorial banner + preferences dialog.
 *
 * Behaviour:
 *  - Never shown on admin/internal routes.
 *  - Never shown when the visitor has hard-opted-out via localStorage flag.
 *  - Never shown after the visitor has made a choice (granted/denied), unless
 *    they re-open preferences via the footer link.
 *  - The banner is a slim card, NOT a modal, so it does not block first-load
 *    content and cannot be construed as a dark pattern.
 *  - Accept All, Reject Non-Essential, and Manage Preferences all have
 *    equal visual weight and minimum 44px touch targets.
 *  - The preferences dialog exposes only two categories: Essential (locked on)
 *    and Analytics (toggleable). Reflects current stored state.
 */
export default function CookieConsent() {
    // Consent state as known to the module ("granted" | "denied" | null).
    const [consent, setConsent] = useState(() => getConsentState());
    const [optOut, setOptOut] = useState(() => isOptedOut());
    // Whether the visitor has explicitly closed the banner (independent of choice).
    const [dismissed, setDismissed] = useState(false);
    const [prefsOpen, setPrefsOpen] = useState(false);
    // Live-editable state used only inside the preferences dialog.
    const [prefsAnalytics, setPrefsAnalytics] = useState(false);
    // Refresh when route changes so we can hide banner on /admin.
    const [pathname, setPathname] = useState(() => {
        try {
            return typeof window !== "undefined" ? window.location.pathname : "/";
        } catch (_e) {
            return "/";
        }
    });

    // Listen for consent + opt-out changes.
    useEffect(() => {
        function onConsentChanged() {
            setConsent(getConsentState());
            setOptOut(isOptedOut());
        }
        window.addEventListener(CONSENT_CHANGE_EVENT, onConsentChanged);
        return () => window.removeEventListener(CONSENT_CHANGE_EVENT, onConsentChanged);
    }, []);

    // Listen for external requests to open preferences (footer link, etc.).
    useEffect(() => {
        function onOpenPrefs() {
            setPrefsAnalytics(getConsentState() === "granted");
            setPrefsOpen(true);
        }
        window.addEventListener(OPEN_COOKIE_PREFS_EVENT, onOpenPrefs);
        return () => window.removeEventListener(OPEN_COOKIE_PREFS_EVENT, onOpenPrefs);
    }, []);

    // Poll for pathname changes cheaply through popstate + a Route observer.
    // We can't `useLocation` here because CookieConsent lives outside <Routes>
    // so we listen to popstate + pushState patch.
    useEffect(() => {
        function syncPath() {
            try {
                setPathname(window.location.pathname);
            } catch (_e) {
                // ignore
            }
        }
        window.addEventListener("popstate", syncPath);

        // Patch pushState/replaceState so SPA route changes update our banner.
        const originalPush = window.history.pushState;
        const originalReplace = window.history.replaceState;
        window.history.pushState = function (...args) {
            const ret = originalPush.apply(this, args);
            syncPath();
            return ret;
        };
        window.history.replaceState = function (...args) {
            const ret = originalReplace.apply(this, args);
            syncPath();
            return ret;
        };

        return () => {
            window.removeEventListener("popstate", syncPath);
            window.history.pushState = originalPush;
            window.history.replaceState = originalReplace;
        };
    }, []);

    const showBanner = useMemo(() => {
        if (dismissed) return false;
        if (optOut) return false;
        if (consent === "granted" || consent === "denied") return false;
        if (!isTrackablePath(pathname)) return false;
        return true;
    }, [dismissed, optOut, consent, pathname]);

    const handleAcceptAll = useCallback(() => {
        grantAnalyticsConsent();
        setDismissed(true);
    }, []);

    const handleRejectNonEssential = useCallback(() => {
        withdrawAnalyticsConsent();
        setDismissed(true);
    }, []);

    const handleOpenPrefs = useCallback(() => {
        setPrefsAnalytics(getConsentState() === "granted");
        setPrefsOpen(true);
    }, []);

    const handleSavePrefs = useCallback(() => {
        setAnalyticsConsent(prefsAnalytics ? "granted" : "denied");
        setPrefsOpen(false);
        setDismissed(true);
    }, [prefsAnalytics]);

    // Preferences dialog can be opened even after a choice has been made (via
    // the footer link). It is rendered regardless of banner visibility.
    return (
        <>
            <AnimatePresence>
                {showBanner ? (
                    <motion.aside
                        data-testid="cookie-consent-banner"
                        role="region"
                        aria-label="Cookie consent"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 24 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none"
                        style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
                    >
                        <div className="pointer-events-auto mx-auto max-w-4xl rounded-2xl border border-white/12 bg-[#0B0C10]/95 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.75)]">
                            <div className="relative p-5 sm:p-6">
                                <div className="flex items-start gap-4">
                                    <div className="hidden sm:flex h-10 w-10 shrink-0 rounded-full border border-white/12 bg-white/5 items-center justify-center text-[#FF2A2A]">
                                        <Cookie size={18} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/60 mb-1.5">
                                            // Cookies & Analytics
                                        </div>
                                        <p className="text-sm sm:text-[15px] text-white/85 leading-relaxed">
                                            We use essential cookies to run this site and
                                            <span className="whitespace-nowrap">&nbsp;optional analytics&nbsp;</span>
                                            to understand how visitors use it. Analytics stays off
                                            unless you say yes.
                                        </p>
                                        <div className="mt-4 flex flex-col sm:flex-row sm:flex-wrap gap-2.5">
                                            <Button
                                                type="button"
                                                onClick={handleAcceptAll}
                                                data-testid="cookie-consent-accept"
                                                className="bg-[#FF2A2A] hover:bg-[#ff4747] text-black font-semibold rounded-full h-11 min-h-11 px-5 min-w-[44px] w-full sm:w-auto"
                                            >
                                                Accept All
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={handleRejectNonEssential}
                                                data-testid="cookie-consent-reject"
                                                className="border-white/25 bg-transparent hover:bg-white/5 text-white rounded-full h-11 min-h-11 px-5 min-w-[44px] w-full sm:w-auto"
                                            >
                                                Reject Non-Essential
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                onClick={handleOpenPrefs}
                                                data-testid="cookie-consent-manage"
                                                className="text-white/80 hover:text-white hover:bg-white/5 rounded-full h-11 min-h-11 px-5 min-w-[44px] w-full sm:w-auto"
                                            >
                                                Manage Preferences
                                            </Button>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        aria-label="Close consent banner"
                                        onClick={() => setDismissed(true)}
                                        data-testid="cookie-consent-close"
                                        className="shrink-0 h-11 w-11 min-h-11 min-w-11 -mr-2 -mt-2 rounded-full text-white/60 hover:text-white hover:bg-white/5 inline-flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF2A2A]"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.aside>
                ) : null}
            </AnimatePresence>

            <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
                <DialogContent
                    data-testid="cookie-preferences-dialog"
                    className="bg-[#0B0C10] border-white/10 text-white sm:max-w-lg"
                >
                    <DialogHeader>
                        <DialogTitle className="text-white font-display text-2xl tracking-tight">
                            Cookie preferences
                        </DialogTitle>
                        <DialogDescription className="text-white/70">
                            Choose which cookies you allow. Essential cookies are required to run
                            the site and cannot be turned off. Analytics helps us understand how
                            visitors use REFORM Pulse. No personal data is sent.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3 mt-2">
                        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-start gap-3">
                            <div className="h-9 w-9 shrink-0 rounded-full border border-white/12 bg-white/5 flex items-center justify-center text-white/80">
                                <Shield size={16} />
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <div className="font-medium text-white text-sm">Essential</div>
                                        <div className="text-xs text-white/60 mt-0.5">
                                            Required for the site to function. Always on.
                                        </div>
                                    </div>
                                    <span
                                        className="text-[10px] font-mono uppercase tracking-widest text-white/50"
                                        data-testid="cookie-prefs-essential-status"
                                    >
                                        Always On
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-start gap-3">
                            <div className="h-9 w-9 shrink-0 rounded-full border border-white/12 bg-white/5 flex items-center justify-center text-[#FF2A2A]">
                                <BarChart3 size={16} />
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <div className="font-medium text-white text-sm">Analytics</div>
                                        <div className="text-xs text-white/60 mt-0.5">
                                            Google Analytics 4 — page views and interaction events.
                                            No names, emails, or form contents are ever sent.
                                        </div>
                                    </div>
                                    <Switch
                                        checked={prefsAnalytics}
                                        onCheckedChange={setPrefsAnalytics}
                                        aria-label="Toggle analytics cookies"
                                        data-testid="cookie-prefs-analytics-toggle"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="mt-4 gap-2 sm:gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setPrefsOpen(false)}
                            className="border-white/25 bg-transparent hover:bg-white/5 text-white rounded-full h-11 min-h-11"
                            data-testid="cookie-prefs-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSavePrefs}
                            className="bg-[#FF2A2A] hover:bg-[#ff4747] text-black font-semibold rounded-full h-11 min-h-11"
                            data-testid="cookie-prefs-save"
                        >
                            Save preferences
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
